import React, { useState, useEffect } from 'react';
import { Container, Table, Spinner, Alert, Badge, Button, Form, InputGroup, Row, Col } from 'react-bootstrap';
import AppNavbar from './Navbar';
import SubmissionModal from './SubmissionModal';
import { supabaseDataService } from '../services/supabaseService';
import { useAuth } from '../contexts/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faEye, faFilter, faSyncAlt, faDownload, faCalendarAlt, faSortUp, faSortDown, faSort, faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import { useDevice } from '../contexts/DeviceContext';
import MobileNavbar from "@/components/MobileNavbar.jsx";
import {useNavigate} from "react-router-dom";
import familyhousehold from "@/assets/images/family.svg";
import finance from "@/assets/images/finance.svg";
import work from "@/assets/images/work.svg";
import extracurriculars from "@/assets/images/extracurricular.svg";
import health from "@/assets/images/health.svg";
import { useNavigation } from '../services/useNavigation';
import _ from "lodash";

const Submissions = () => {
    const [submissions, setSubmissions] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [groupBy, setGroupBy] = useState('all');
    const [showModal, setShowModal] = useState(false);
    const [selectedSubmission, setSelectedSubmission] = useState(null);
    const { isMobile, isTablet, isDesktop, windowWidth } = useDevice();
    const navigate = useNavigate();
    const { handleNavItemClick } = useNavigation();
    const iconMap = {
        familyhousehold: familyhousehold,
        finance: finance,
        work: work,
        extracurriculars: extracurriculars,
        health: health
    };

    const [activeComponent, setActiveComponent] = useState('memory');

    const { user, getCategories } = useAuth();

    const [sortConfig, setSortConfig] = useState({
        key: 'created_at',
        direction: 'desc'
    });

    const handleViewSubmission = (submission) => {
        setSelectedSubmission(submission);
        setShowModal(true);
    };

    const handleModalClose = () => {
        setShowModal(false);
    };

    const handleSubmissionDelete = async (submission) => {
        console.log('[handleSubmissionDelete]----->', submission);
        await supabaseDataService.deleteSubmission(submission.submission_id);
        setShowModal(false);
        await fetchSubmissions();
    }

    useEffect(() => {
        fetchSubmissions();
    }, []);
    useEffect(() => {
        fetchCategories()
    }, []);

    const fetchCategories = async () => {
        try {
            const categories = await getCategories();
            setCategories(categories);
            setError('');
        } catch (err) {
            setError('Failed to load categories. Please try again later.');
            console.error(err);
        } finally {
        }
    };

    const fetchSubmissions = async () => {
        try {
            setLoading(true);
            const data = await supabaseDataService.getSubmissions(user.id, 0, 100);
            setSubmissions(data);
            setError('');
        } catch (err) {
            setError('Failed to load submissions. Please try again later.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (columnName) => {
        if (sortConfig.key !== columnName) {
            return <FontAwesomeIcon icon={faSort} className="ms-1 text-muted" />;
        }
        return sortConfig.direction === 'asc'
            ? <FontAwesomeIcon icon={faSortUp} className="ms-1" />
            : <FontAwesomeIcon icon={faSortDown} className="ms-1" />;
    };

    const filteredSubmissions = submissions.filter(sub => {
        const matchesSearch = searchTerm === '' ||
            (sub.info && sub.info.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (sub.summary && sub.summary.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (sub.category && sub.category.toLowerCase().includes(searchTerm.toLowerCase()));

        const matchesCategory = categoryFilter === 'all' || _.find(sub.submissions_categories,{category_id:categoryFilter});
        return matchesSearch && matchesCategory;
    });

    const sortedSubmissions = [...filteredSubmissions].sort((a, b) => {
        if (a[sortConfig.key] === null) return 1;
        if (b[sortConfig.key] === null) return -1;

        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Convert to lowercase for string comparison
        if (typeof aValue === 'string') {
            aValue = aValue.toLowerCase();
            bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) {
            return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
            return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
    });

    const getGroupedSubmissions = () => {
        if (groupBy === 'all') {
            return { 'All Submissions': sortedSubmissions };
        }
        const grouped = {};

        sortedSubmissions.forEach(submission => {
            let groupKey;
            if ( _.find(submission.group_submissions,{group_id:groupBy}) ) {
                groupKey = submission.submissions_categories[0].category_id
                if (!grouped[groupKey]) {
                    grouped[groupKey] = [];
                }
                grouped[groupKey].push(submission);
            }
        });

        return grouped;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    const chooseIcon = (categories, submissionId) => {
        const icons = [];
        let index = 0;
        categories.forEach((category) => {

            if (iconMap[category.category_id]) {
                let tkey = index + '' + submissionId;
                icons.push(
                    <img
                        key={tkey}
                        src={iconMap[category.category_id]}
                        alt={category.category_id}
                        style={{maxWidth: '20px', height: 'auto'}}
                        title={category.category_id}
                    />
                );
                index++;
            }
        });
        return icons;
    };

    const renderGroupedTable = () => {
        const groupedSubmissions = getGroupedSubmissions();
        const groupKeys = Object.keys(groupedSubmissions).sort();

        return groupKeys.map(groupKey => (
            <div key={groupKey} className="mb-4">
                <div className="table-responsive">
                    <Table striped hover className="submission-table mb-0">
                        <thead>
                        <tr key={`header-${groupKey}`}>
                            <th onClick={() => requestSort('category')} className="sortable-header category-header">
                                Category {getSortIcon('category')}
                            </th>
                            <th onClick={() => requestSort('summary')} className="sortable-header">
                                Summary {getSortIcon('summary')}
                            </th>
                            {!isMobile && (
                                <th>
                                    Info
                                </th>
                            )}
                            {!isMobile && (
                                <th onClick={() => requestSort('created_at')} className="sortable-header">
                                    Created {getSortIcon('created_at')}
                                </th>
                            )}
                            <th></th>
                        </tr>
                        </thead>
                        <tbody>
                        {groupedSubmissions[groupKey].map((submission) => (
                            <tr key={submission.submission_id}>
                                <td>
                                    <div className="flex-shrink-0 me-4">
                                        {chooseIcon(submission.submissions_categories, submission.submission_id)}
                                    </div>
                                </td>
                                <td>{submission.summary || ''}</td>
                                {!isMobile && (
                                    <td>{submission.info || ''}</td>
                                )}
                                {!isMobile && (
                                    <td>{formatDate(submission.created_at)}</td>
                                )}
                                <td>
                                    <i className="submission-detail"
                                       onClick={() => handleViewSubmission(submission)}>
                                        <FontAwesomeIcon icon={faEye}/>
                                    </i>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </Table>
                </div>
            </div>
        ));
    };

    if (loading && submissions.length === 0) {
        return (
            <>
                <AppNavbar/>
                <Container className="text-center mt-5">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </Container>
            </>
        );
    }

    return (
        <div className="dashboard-wrapper submissions">
            <div className="d-none d-md-block">
                <AppNavbar/>
            </div>

            {/* Mobile Navbar */}
            <MobileNavbar
                onNavItemClick={handleNavItemClick}
                activeItem={activeComponent}
            />
            <Container className="py-4">
                {error && <Alert variant="danger">{error}</Alert>}

                {/* Filters and Group Selector */}
                <Row className="mb-4">
                    <Col md={4} className="mb-3">
                        <InputGroup>
                            <InputGroup.Text>
                                <FontAwesomeIcon icon={faSearch} />
                            </InputGroup.Text>
                            <Form.Control
                                type="text"
                                placeholder="Search submissions..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                            />
                        </InputGroup>
                    </Col>
                    <Col md={3} className="mb-3">
                        <Form.Select
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                        >
                            <option value="all">All Categories</option>
                            {categories.map(category => (
                                <option key={category.id} value={category.id}>
                                    {category.label || category.name || category.category_id}
                                </option>
                            ))}
                        </Form.Select>
                    </Col>
                    <Col md={3} className="mb-3">
                        <Form.Select
                            value={groupBy}
                            onChange={(e) => setGroupBy(e.target.value)}
                        >
                            <option value="all">
                                All
                            </option>
                            <option value={user.groupId}>Private</option>
                        </Form.Select>
                    </Col>
                </Row>

                {filteredSubmissions.length === 0 ? (
                    <Alert variant="info">No submissions found matching your criteria.</Alert>
                ) : (
                    renderGroupedTable()
                )}

                {/* Submission Modal */}
                <SubmissionModal
                    submission={selectedSubmission}
                    show={showModal}
                    onHide={handleModalClose}
                    onDelete={handleSubmissionDelete}
                />
            </Container>
        </div>
    );
};

export default Submissions;